import { apiClient } from '@littlebird/auth-client';
import { useQuery } from '@tanstack/react-query';
import { addDays } from 'date-fns/addDays';
import { subMilliseconds } from 'date-fns/subMilliseconds';
import { z } from 'zod';

const ContextItemSchema = z.object({
  id: z.string(),
  text: z.string(),
  created_at: z.string(),
  text_size: z.number(),
  metadata: z
    .object({
      start: z.string().nullish(),
      end: z.string().nullish(),
    })
    .nullish(),
});

const UserContextResponseSchema = z.object({
  results: z.array(ContextItemSchema),
});

export interface SummaryTimelineItem {
  uuid: string;
  start: string | null;
  end: string | null;
  created_at: string;
  text: string;
  chars: number;
}

export function useSummaryTimelineQuery(day: Date) {
  return useQuery({
    queryKey: ['summaries', day.toDateString()],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<SummaryTimelineItem[]> => {
      const response = await apiClient.get('/debug/user_context/', {
        params: {
          tables: 'summary',
          after: day.toISOString(),
          before: subMilliseconds(addDays(day, 1), 1).toISOString(),
          sortBy: 'recent',
          limit: 100,
        },
      });
      const { results } = await UserContextResponseSchema.parseAsync(response);
      return results.map((item) => ({
        uuid: item.id,
        start: item.metadata?.start ?? null,
        end: item.metadata?.end ?? null,
        created_at: item.created_at,
        text: item.text,
        chars: item.text_size,
      }));
    },
  });
}

const InputAppsResponseSchema = z.object({
  results: z.array(z.object({ app: z.string() })),
});

export interface SummaryApp {
  app: string;
  count: number;
}

export function useSummaryAppsQuery(item: SummaryTimelineItem) {
  return useQuery({
    queryKey: ['summaries', item.uuid, 'apps'],
    enabled: item.start !== null && item.end !== null,
    staleTime: Infinity,
    queryFn: async (): Promise<SummaryApp[]> => {
      if (item.start === null || item.end === null) return [];
      const response = await apiClient.get('/debug/user_context/', {
        params: {
          tables: 'input',
          after: item.start,
          before: item.end,
          limit: 100,
        },
      });
      const { results } = await InputAppsResponseSchema.parseAsync(response);
      const counts = new Map<string, number>();
      for (const { app } of results) {
        counts.set(app, (counts.get(app) ?? 0) + 1);
      }
      return [...counts]
        .map(([app, count]) => ({ app, count }))
        .sort((a, b) => b.count - a.count);
    },
  });
}
